import type { KeenSliderPlugin } from 'keen-slider'
import type { MutableRefObject, RefObject } from 'react'

export type KeenAutoplayControl = {
  schedule: () => void
  pause: () => void
  resume: () => void
  clear: () => void
}

/**
 * Keen autoplay plugin with CSS-loader sync (hero dots) or setTimeout fallback.
 * Restarts after tab visibility changes and Keen resize/update so autoplay does
 * not stall when animations or timers are interrupted.
 */
export function keenAutoplayPlugin(
  intervalMs: number,
  onLoaderStart: () => void,
  onAdvance: () => void,
  isPlayingRef: RefObject<boolean>,
  controlRef: MutableRefObject<KeenAutoplayControl | null>,
  useLoaderSync: boolean,
): KeenSliderPlugin {
  return (slider) => {
    let timeout: ReturnType<typeof setTimeout>
    let slideStartedAt = 0
    let remainingMs: number | null = null
    // Track abs index so click/tap settle animations don't restart the progress ring.
    let lastAbs: number | null = null
    let rafUpdate = 0

    const clearTimer = () => clearTimeout(timeout)

    const beginSlideTimer = (delay: number) => {
      clearTimer()
      if (!isPlayingRef.current) return
      slideStartedAt = Date.now()
      timeout = setTimeout(() => {
        if (isPlayingRef.current) onAdvance()
      }, delay)
    }

    const schedule = () => {
      clearTimer()
      onLoaderStart()
      remainingMs = null
      lastAbs = slider.track.details.abs
      if (!useLoaderSync && isPlayingRef.current) beginSlideTimer(intervalMs)
    }

    const pause = () => {
      if (timeout) {
        const elapsed = Date.now() - slideStartedAt
        remainingMs = Math.max(0, Math.min(intervalMs, intervalMs - elapsed))
        clearTimer()
      }
    }

    const resume = () => {
      if (!isPlayingRef.current) return
      if (useLoaderSync) return
      beginSlideTimer(remainingMs ?? intervalMs)
      remainingMs = null
    }

    const restartIfPlaying = () => {
      if (!isPlayingRef.current) return
      if (useLoaderSync) schedule()
      else resume()
    }

    const scheduleAfterLayout = () => {
      if (rafUpdate) return
      rafUpdate = requestAnimationFrame(() => {
        rafUpdate = 0
        restartIfPlaying()
      })
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        pause()
        return
      }
      restartIfPlaying()
    }

    controlRef.current = { schedule, pause, resume, clear: clearTimer }

    slider.on('created', schedule)
    slider.on('dragStarted', clearTimer)
    slider.on('animationEnded', () => {
      if (lastAbs !== null && slider.track.details.abs === lastAbs) return
      schedule()
    })
    slider.on('updated', scheduleAfterLayout)

    document.addEventListener('visibilitychange', onVisibilityChange)
    slider.on('destroyed', () => {
      clearTimer()
      cancelAnimationFrame(rafUpdate)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    })
  }
}
